// ============================================================
// FEATURE: Transmitter — a cross-window DIAL + specimen launcher.
//
// A SENDER. Turning its knob HERE swings the needle gauge in a
// SEPARATE PowerMonitor window, live. It also launches "specimens":
// pressing TRANSMIT fires a tagged blip that arrives and crawls
// across the receiver in the other window.
//
// Publishes onto the shared windowBus:
//   signal.freq      : number 0..1   (continuous, from the knob)
//   signal.specimen  : { t, kind }   (one-shot launch stamp)
//
// The knob is dragged with the pointer; we coarsely map vertical
// drag to 0..1 and publish on every change. A pointermove listener
// is added to window only WHILE dragging and removed on pointerup /
// unmount, so nothing leaks.
//
// Non-draggable content component — mount as the artComp of an
// AnnexWindow. (Its own pointer drag is on the KNOB only, so it
// won't fight the AnnexWindow titlebar drag.)
// ============================================================

import * as React from "react";
import "./transmitter.css";
import windowBus from "../_shared/windowBus";
import { tactile, payoff } from "../_shared/feedback";

// How many detent notches span the full 0..1 dial. The knob clicks once per
// notch crossed while dragging, the way a stepped potentiometer feels.
const DETENT_STEPS = 24;

// The little roster of specimens you can launch. Dull/clinical names.
const SPECIMENS = ["MOTH", "TOOTH", "FILAMENT", "VERTEBRA", "GNAT", "RELAY"];

class Transmitter extends React.Component {
  constructor(props) {
    super(props);

    const seeded = windowBus.getState("signal.freq");

    this.state = {
      freq: typeof seeded === "number" ? clamp01(seeded) : 0.5,
      dragging: false,
      kindIndex: 0, // which specimen is loaded
    };

    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this.startDrag = this.startDrag.bind(this);
    this.transmit = this.transmit.bind(this);
    this.cycleKind = this.cycleKind.bind(this);

    // Drag bookkeeping captured at pointerdown.
    this._dragStartY = 0;
    this._dragStartFreq = 0.5;
    // Last detent notch we clicked at, so a drag only blips when it crosses a
    // fresh notch rather than on every sub-pixel move.
    this._lastDetent = detentStep(this.state.freq);
  }

  // Give one tactile answer for a control interaction. props.sound is threaded
  // from the Annex's global mute; when false the audio blip is skipped but the
  // haptic may still fire.
  _feedback(kind) {
    tactile({ kind, sound: this.props.sound !== false });
  }

  componentDidMount() {
    // Announce our starting dial so a receiver already open agrees with us.
    windowBus.publish("signal.freq", this.state.freq);
  }

  componentWillUnmount() {
    // Make sure no stray window listeners survive us.
    this._detachWindow();
  }

  _attachWindow() {
    if (typeof window === "undefined") return;
    window.addEventListener("pointermove", this._onPointerMove);
    window.addEventListener("pointerup", this._onPointerUp);
  }

  _detachWindow() {
    if (typeof window === "undefined") return;
    window.removeEventListener("pointermove", this._onPointerMove);
    window.removeEventListener("pointerup", this._onPointerUp);
  }

  startDrag(e) {
    // Begin a knob drag. Capture the start point + value, then listen on
    // window so the drag keeps tracking even if the pointer leaves the knob.
    const y = pointerY(e);
    this._dragStartY = y;
    this._dragStartFreq = this.state.freq;
    this._lastDetent = detentStep(this.state.freq);
    this.setState({ dragging: true });
    this._attachWindow();
  }

  _onPointerMove(e) {
    if (!this.state.dragging) return;
    const y = pointerY(e);
    // Drag UP raises the value. ~160px of travel spans the full 0..1 range.
    const delta = (this._dragStartY - y) / 160;
    const next = clamp01(this._dragStartFreq + delta);
    if (next !== this.state.freq) {
      // Click once per detent notch crossed so turning the knob feels stepped.
      const step = detentStep(next);
      if (step !== this._lastDetent) {
        this._lastDetent = step;
        this._feedback("detent");
      }
      this.setState({ freq: next });
      windowBus.publish("signal.freq", next);
    }
  }

  _onPointerUp() {
    if (!this.state.dragging) return;
    this.setState({ dragging: false });
    this._detachWindow();
  }

  cycleKind() {
    // Loading the next specimen reads as a soft two-step chunk.
    this._feedback("load");
    this.setState((s) => ({ kindIndex: (s.kindIndex + 1) % SPECIMENS.length }));
  }

  transmit() {
    // Fire a one-shot specimen launch. The timestamp makes each press a fresh,
    // distinct event for the receiver across the window gap.

    // TRANSMIT is an ACTIVATION — answer it with the bigger payoff (rolling
    // haptic + layered launch burst), not just the small launch click.
    payoff({ kind: "transmit", sound: this.props.sound !== false });

    // Keep the existing specimen mechanic — it still crawls across the monitor.
    windowBus.publish("signal.specimen", {
      t: Date.now(),
      kind: SPECIMENS[this.state.kindIndex],
    });

    // Broadcast the dramatic cross-window "overload" so the PowerMonitor (and
    // any listener) ERUPTS on transmit too: screen-flash, meters slamming to
    // max + pinning, a shockwave racing out, readouts spiking then settling.
    // intensity scales with the current dial so a hotter dial hits harder.
    windowBus.publish("power.overload", {
      t: Date.now(),
      source: "transmit",
      intensity: 0.55 + clamp01(this.state.freq) * 0.45,
    });
  }

  render() {
    const { freq, dragging, kindIndex } = this.state;
    // Knob rotates over a 270° span so its notch reads the value at a glance.
    const knobAngle = -135 + freq * 270;
    const pct = Math.round(freq * 100);

    return (
      <div className={"tx-rig" + (dragging ? " tx-dragging" : "")}>
        <div className="tx-head">
          <span className="tx-head-text">TRANSMITTER</span>
          <span className="tx-head-pct">{pct}%</span>
        </div>

        {/* the knob — drag up/down to set the dial */}
        <div className="tx-knob-wrap">
          <div
            className="tx-knob"
            style={{ transform: "rotate(" + knobAngle + "deg)" }}
            onPointerDown={this.startDrag}
            role="slider"
            aria-label="Transmit frequency"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            tabIndex={0}
            title="Drag up / down to turn"
          >
            <span className="tx-knob-notch" aria-hidden="true" />
            <span className="tx-knob-grip" aria-hidden="true" />
          </div>
          <div className="tx-knob-scale" aria-hidden="true">
            <span className="tx-scale-min">0</span>
            <span className="tx-scale-max">MAX</span>
          </div>
        </div>

        {/* specimen launcher */}
        <div className="tx-launch">
          <button
            type="button"
            className="tx-load"
            onClick={this.cycleKind}
            title="Load next specimen"
          >
            <span className="tx-load-label">LOAD</span>
            <span className="tx-load-kind">{SPECIMENS[kindIndex]}</span>
          </button>
          <button
            type="button"
            className="tx-fire"
            onClick={this.transmit}
            title="Transmit the loaded specimen to the other window"
          >
            TRANSMIT ►
          </button>
        </div>

        <div className="tx-foot">
          DIAL DRIVES A NEEDLE IN A SEPARATE WINDOW
        </div>
      </div>
    );
  }
}

function clamp01(n) {
  if (typeof n !== "number" || !isFinite(n)) return 0;
  if (n <= 0) return 0;
  if (n >= 1) return 1;
  return n;
}

// Which detent notch a 0..1 dial value falls in. Used so the knob clicks once
// per notch crossed during a drag instead of continuously.
function detentStep(n) {
  return Math.round(clamp01(n) * DETENT_STEPS);
}

// Pull a Y coordinate out of a pointer/mouse/touch event, defensively.
function pointerY(e) {
  if (!e) return 0;
  if (typeof e.clientY === "number") return e.clientY;
  if (e.touches && e.touches[0]) return e.touches[0].clientY;
  return 0;
}

export default Transmitter;
